import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Link,
    Paper,
    Skeleton,
    Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import CodebookPanel from '../coding/CodebookPanel';
import { getCodebook, getQueue } from '../../services/adjudicationService';
import { getAnnotationCodebook } from '../../services/annotationService';
import { Progress } from './parts';

// One card per run (wf-adj-queue 1, 3): name, schedule, progress as a count, and Next item.
// Never a list of items, their order, flags or why they were chosen.
// A run's status is setup, open or closed; the caller's own work is done when every assignment is
const isDone = (card) => (card.kind === 'lesson'
    ? card.state === 'submitted'
    : card.status === 'closed' || (card.total > 0 && card.done === card.total));
const keyOf = (card) => card.runId || card.assignmentId;
const nameOf = (card) => card.name || card.label;
const shortDate = (iso) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// The card's kind, shown when lesson and run cards share the page (wf-annotate-lesson-1)
const Kind = ({ children }) => <Typography variant="overline" component="p" color="text.secondary" sx={{ lineHeight: 1.6 }}>{children}</Typography>;

// A lesson to annotate (wf-annotate-lesson 1, 4): a neutral label, never the teacher's lesson name;
// Continue opens the sheet at the first uncoded utterance; a submitted lesson stays as a record
const LessonCard = ({ card, showKind }) => {
    const submitted = card.state === 'submitted';
    return (
        <Paper component="article" aria-labelledby={`lesson-${card.assignmentId}`} sx={{ p: { xs: 2.5, sm: 3 } }}>
            {showKind && <Kind>Lesson to annotate</Kind>}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography id={`lesson-${card.assignmentId}`} variant={submitted ? 'h5' : 'h4'} component="h2">{card.label}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {submitted ? `Submitted ${shortDate(card.submittedAt)} · your codes are final` : `Code every utterance · assigned ${shortDate(card.assignedAt)}`}
                    </Typography>
                    {!submitted && <Box sx={{ mt: 2.5 }}><Progress done={card.coded} total={card.total} width={440} /></Box>}
                </Box>
                {!submitted && (
                    <Button variant="contained" size="large" component={RouterLink} to={`/annotate/${encodeURIComponent(card.assignmentId)}`} sx={{ flexShrink: 0 }}>
                        Continue
                    </Button>
                )}
            </Box>
            {!submitted && (
                <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 2 }}>
                    {card.savedAt ? `Draft saved ${shortDate(card.savedAt)} · ` : ''}editable until you submit
                </Typography>
            )}
        </Paper>
    );
};

const RunCard = ({ card, showKind }) => {
    const done = isDone(card);
    const notStarted = card.status === 'setup';
    return (
        <Paper component="article" aria-labelledby={`run-${card.runId}`} sx={{ p: { xs: 2.5, sm: 3 } }}>
            {showKind && <Kind>Adjudication run</Kind>}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: done ? 'flex-start' : 'center' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography id={`run-${card.runId}`} variant={done ? 'h5' : 'h4'} component="h2">{card.name}</Typography>
                    {card.schedule && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{card.schedule}</Typography>}
                    {!done && !notStarted && <Box sx={{ mt: 2.5 }}><Progress done={card.done} total={card.total} width={440} /></Box>}
                </Box>
                {notStarted ? null : done ? (
                    <Progress done={card.done} total={card.total} width={160} complete />
                ) : (
                    <Button variant="contained" size="large" component={RouterLink} to={`/adjudicate/${encodeURIComponent(card.runId)}`} sx={{ flexShrink: 0 }}>
                        Next item
                    </Button>
                )}
            </Box>
            <Typography variant="caption" component="p" color="text.secondary" sx={{ mt: 2 }}>
                {notStarted
                    ? 'This run hasn’t started yet. Its items appear here when it does.'
                    : done
                        ? 'Thank you. If an item needs a third adjudicator, it is added here.'
                        : 'You can stop at any point; Next item brings you back to where you stopped.'}
            </Typography>
        </Paper>
    );
};

const Step = ({ n, children }) => (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <Box component="span" aria-hidden sx={{ width: 22, height: 22, flexShrink: 0, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: 'action.selected', color: 'primary.main', fontSize: '0.6875rem', fontWeight: 700 }}>{n}</Box>
        <Typography variant="body2">{children}</Typography>
    </Box>
);

const Block = ({ title, children }) => (
    <Box component="section" sx={{ display: 'grid', gap: 0.75 }}>
        <Typography variant="subtitle2" component="h3">{title}</Typography>
        {children}
    </Box>
);

const Bullets = ({ items }) => (
    <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.5, '& li': { typography: 'body2' } }}>
        {items.map((item) => <li key={item}>{item}</li>)}
    </Box>
);

// About your work (wf-adj-queue aside, decision 2026-10-25): static text, the same in every state.
// Never says how or why items are chosen. Codebook links only when there are runs.
const AboutYourWork = ({ cards, onOpenCodebook }) => {
    const lessons = cards.some((c) => c.kind === 'lesson');
    const runs = cards.some((c) => c.kind !== 'lesson');
    return (
    <Paper component="aside" aria-labelledby="about-work" sx={{ p: 3, display: 'grid', gap: 2.5 }}>
        <Box sx={{ display: 'grid', gap: 0.75 }}>
            <Typography id="about-work" variant="h5" component="h2">About your work</Typography>
            <Typography variant="body2" color="text.secondary">
                ADAS supports research on classroom talk: you code utterances from K-12 lesson transcripts with a shared codebook.
            </Typography>
        </Box>
        {cards.length > 0 && (
            <Block title="Your role">
                {lessons && (
                    <>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Annotating a lesson</Typography>
                        <Step n={1}>Code UT and RC for every utterance, in order</Step>
                        <Step n={2}>Your codes are drafts; change them until you submit</Step>
                        <Step n={3}>Submit when every utterance is coded</Step>
                        <Typography variant="caption" color="text.secondary">Submitted codes are final.</Typography>
                    </>
                )}
                {runs && (
                    <>
                        {lessons && <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>Adjudicating an item</Typography>}
                        <Step n={1}>Read the utterance in its context</Step>
                        <Step n={2}>Code UT and RC, then lock</Step>
                        <Step n={3}>See the earlier label; give a reason if they differ</Step>
                        <Typography variant="caption" color="text.secondary">
                            A locked code cannot be changed. If an item needs a third adjudicator, they decide it; there is nothing more for you to do.
                        </Typography>
                    </>
                )}
            </Block>
        )}
        <Block title="What we ask of you">
            <Bullets items={lessons ? [
                'Work on your own; don’t discuss lessons or items with other coders.',
                'Code each utterance as it is written, in its context.',
                'Unsure? Pick the closest code; on an adjudication item, also mark it unsure.',
                'Stop at any time; Continue and Next item bring you back to where you stopped.',
            ] : [
                'Work on your own; don’t discuss items with other adjudicators.',
                'Code the utterance as it is written, in its context.',
                'Unsure? Pick the closest code and mark it unsure.',
                'Stop at any time; Next item brings you back to where you stopped.',
            ]}
            />
        </Block>
        <Block title="Confidentiality">
            <Bullets items={[
                'These transcripts come from real classrooms with children. They are de-identified; keep them private all the same.',
                'Don’t copy, screenshot or share any text, and don’t discuss it outside the study team.',
                'Sign out when you use a shared computer.',
            ]}
            />
        </Block>
        {cards.length > 0 && (
            <Block title="Codebook">
                <Typography variant="body2" color="text.secondary">
                    {lessons ? 'Every UT and RC code with its definition. Each lesson and run has its own codebook version.' : 'Every UT and RC code with its definition. Each run has its own codebook version.'}
                </Typography>
                {cards.map((card) => (
                    <Link key={keyOf(card)} component="button" type="button" underline="hover" onClick={() => onOpenCodebook(card)} sx={{ textAlign: 'left', fontWeight: 600, fontSize: '0.875rem' }}>
                        {cards.length === 1 ? 'Open codebook' : `Codebook · ${nameOf(card)}`}
                    </Link>
                ))}
            </Block>
        )}
    </Paper>
    );
};

// wf-adj-queue-4: the run's codebook; closes with ✕, Escape or a click outside
const CodebookDialog = ({ card, onClose }) => {
    const [codebook, setCodebook] = useState(null);
    const [failed, setFailed] = useState(false);

    const load = useCallback(async () => {
        setFailed(false);
        setCodebook(null);
        try {
            setCodebook(await (card.kind === 'lesson' ? getAnnotationCodebook(card.assignmentId) : getCodebook(card.runId)));
        } catch (err) {
            setFailed(true);
        }
    }, [card]);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="codebook-title" scroll="paper">
            <DialogTitle id="codebook-title" sx={{ pr: 6 }}>
                Codebook · {nameOf(card)}
                <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                {failed && (
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>
                        Couldn’t load the codebook.
                    </Alert>
                )}
                {!failed && !codebook && [0, 1, 2, 3].map((i) => <Skeleton key={i} height={48} />)}
                {codebook && <CodebookPanel codebook={codebook} speaker="unknown" sx={{ border: 'none', p: 0 }} />}
            </DialogContent>
        </Dialog>
    );
};

// /adjudicate, the adjudicator's landing page (wf-adj-queue 1–4, YES-255)
const MyQueue = () => {
    const [cards, setCards] = useState(null);
    const [failed, setFailed] = useState(false);
    const [codebookFor, setCodebookFor] = useState(null);

    const load = useCallback(async () => {
        setFailed(false);
        setCards(null);
        try {
            const list = await getQueue();
            // Runs with work left come first
            setCards([...list].sort((a, b) => isDone(a) - isDone(b)));
        } catch (err) {
            setFailed(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    return (
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
            <Typography variant="h2" component="h1" sx={{ mt: { xs: 1, sm: 2 } }}>My queue</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                Items come one at a time, in a fixed order. Code each one before you see anything else about it.
            </Typography>

            {failed ? (
                <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Try again</Button>}>
                    Couldn’t load your queue.
                </Alert>
            ) : (
                <Grid container spacing={2.5} alignItems="flex-start">
                    <Grid item xs={12} md={8} sx={{ display: 'grid', gap: 2 }}>
                        {!cards && [0, 1].map((i) => <Skeleton key={i} variant="rounded" height={i ? 110 : 190} />)}
                        {cards && cards.length === 0 && (
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h5" component="h2">No items yet</Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    You will see your items here when a run you are part of opens.
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Questions? Contact the study team.
                                </Typography>
                            </Paper>
                        )}
                        {cards && cards.map((card) => (card.kind === 'lesson'
                            ? <LessonCard key={keyOf(card)} card={card} showKind={cards.some((c) => c.kind !== 'lesson')} />
                            : <RunCard key={keyOf(card)} card={card} showKind={cards.some((c) => c.kind === 'lesson')} />))}
                    </Grid>
                    <Grid item xs={12} md={4}>
                        {cards ? <AboutYourWork cards={cards} onOpenCodebook={setCodebookFor} /> : <Skeleton variant="rounded" height={420} />}
                    </Grid>
                </Grid>
            )}
            {codebookFor && <CodebookDialog card={codebookFor} onClose={() => setCodebookFor(null)} />}
        </Box>
    );
};

export default MyQueue;
